import * as React from 'react';
import refreshIcon from '@kaizen/component-library/icons/refresh.icon.svg';
import { Paragraph } from '@kaizen/typography';
import { InlineNotification } from '@kaizen/notification';
import { Button } from '@kaizen/button';
import { ConfirmationModal } from '@kaizen/draft-modal';

import csrfParam from 'ca-environment/csrfParam';
import csrfToken from 'ca-environment/csrfToken';

import styles from './ResetResponseButton.scss';
import { FormattedMessage, useIntl } from '@cultureamp/i18n-react-intl';

export const ResetResponseButton = ({
  resetResponseUrl,
  surveyName,
  redoText,
  redoSuccessMessageTitle,
  redoSuccessMessage,
}: {
  resetResponseUrl: string;
  surveyName: string;
  redoText: string | null;
  redoSuccessMessageTitle: string | null;
  redoSuccessMessage: string | null;
}) => {
  const [isOpen, setOpen] = React.useState(false);

  const [didReset, setDidReset] = React.useState(false);
  const [didNotReset, setDidNotReset] = React.useState(false);
  const { formatMessage } = useIntl();

  const resolvedRedoText = redoText
    ? redoText
    : formatMessage({
        id: 'redo_survey.redo_survey',
        defaultMessage: 'Redo survey',
      });
  const resolvedRedoSuccessMessageTitle = redoSuccessMessageTitle
    ? redoSuccessMessageTitle
    : formatMessage({
        id: 'redo_survey.success_title',
        defaultMessage: 'A new survey link is on its way',
      });

  const resolvedRedoSuccessMessage = redoSuccessMessage
    ? redoSuccessMessage
    : formatMessage({
        id: 'redo_survey.success',
        defaultMessage:
          'Check your inbox for a new email to redo your survey. Make sure to use the new link, as your original survey link will no longer be valid.',
      });

  return (
    <>
      <Button
        label={resolvedRedoText}
        secondary
        reversed
        icon={refreshIcon}
        onClick={() => {
          setOpen(true);
        }}
      />
      <ConfirmationModal
        confirmLabel={formatMessage({
          id: 'redo_survey.confirm_button_label',
          defaultMessage: 'Confirm',
        })}
        dismissLabel={formatMessage({
          id: 'redo_survey.dismiss_button_label',
          defaultMessage: 'Cancel',
        })}
        isOpen={isOpen}
        mood="negative"
        isProminent
        title={formatMessage({
          id: 'redo_survey.confirm_title',
          defaultMessage: 'Delete responses and redo survey',
        })}
        onDismiss={() => setOpen(false)}
        onConfirm={() => {
          setOpen(false);
          setDidReset(false);
          setDidNotReset(false);
          fetch(resetResponseUrl, {
            method: 'POST',
            body: JSON.stringify({
              [csrfParam()]: csrfToken(),
            }),
          })
            .then((response) => response.json())
            .then((data) => {
              if (data.ok) {
                // Ok.. show notification
                setDidReset(true);
              } else {
                // Not ok.. show error notification
                setDidNotReset(true);
              }
              setOpen(false);
            })
            .catch((e) => {
              setOpen(false);
              throw e;
            });
        }}
      >
        <div className={styles.redoInfo}>
          <Paragraph variant={'intro-lede'}>
            <FormattedMessage
              id="redo_survey.redo_info.headline"
              defaultMessage={`This will allow you to complete the "{surveyName}" survey again.`}
              values={{ surveyName }}
            />
          </Paragraph>
          <Paragraph variant={'intro-lede'}>
            <FormattedMessage
              id="redo_survey.redo_info.text"
              defaultMessage="Your existing responses will be deleted and you will receive a new invitation to complete the survey."
            />
          </Paragraph>
        </div>
      </ConfirmationModal>
      {didReset && (
        <div className={styles.notification}>
          <InlineNotification
            type="informative"
            title={resolvedRedoSuccessMessageTitle}
          >
            {resolvedRedoSuccessMessage}
          </InlineNotification>
        </div>
      )}
      {didNotReset && (
        <div className={styles.notification}>
          <InlineNotification
            type="cautionary"
            title={formatMessage({
              id: 'redo_survey.failure_title',
              defaultMessage: 'Your response could not be reset',
            })}
          >
            <FormattedMessage
              id="redo_survey.redo_failure.text"
              defaultMessage="Sorry! Your response could not be reset."
            />
          </InlineNotification>
        </div>
      )}
    </>
  );
};
